import React from "react";
import { createContext, useEffect, useState } from "react";
import { getUserWithClientAccesses } from "../../services/fetch";
import { ClientAccess, GlobalUserRole, UserWithClients } from "./types";
import RequestInterceptor from "../AuthContext/RequestInterceptor"; // Make sure to import RequestInterceptor

export interface IClientAccessProvider {
	selectedClient: ClientAccess;
	userWithClients: UserWithClients;
	loadingClients: boolean;
	setSelectedClient: React.Dispatch<React.SetStateAction<ClientAccess>>;
}

interface ClientAccessProps {
	children?: React.ReactNode;
}

const ClientAccessContext = createContext({} as IClientAccessProvider);

const ClientAccessProvider: React.FunctionComponent<ClientAccessProps> = ({
	children,
}) => {
	const [loadingClients, setLoadingClients] = useState(true);
	const [userWithClients, setUserWithClients] =
		useState<null | UserWithClients>(null);
	const [selectedClient, setSelectedClient] = useState<null | ClientAccess>(
		null
	);

	useEffect(() => {
		const fetchClientAccesses = async () => {
			setLoadingClients(true);
			const response = await getUserWithClientAccesses();
			const user = response.result;
			if (user.globalRole) {
				user.globalRole = user.globalRole.trim() as GlobalUserRole;
			}
			setUserWithClients(user);
			if (!user.clientAccesses?.length)
				console.error("user does not have any client access");
			if (user.clientAccesses.length === 1) {
				setSelectedClient(user.clientAccesses[0]);
			} else {
				const clientStorageKey = `${user.id}-selectedClientId`;
				const storedSelectedClientId = localStorage.getItem(clientStorageKey);
				const clientFound = user.clientAccesses.find(
					(a) => a.clientId === +storedSelectedClientId
				);
				if (storedSelectedClientId && clientFound) {
					setSelectedClient(clientFound);
				}
			}
			setLoadingClients(false);
		};
		fetchClientAccesses();
	}, []);

	useEffect(() => {
		if (!selectedClient) return;
		const clientStorageKey = `${userWithClients.id}-selectedClientId`;
		localStorage.setItem(clientStorageKey, selectedClient.clientId.toString());
	}, [selectedClient]);

	const clientAccessContextValue = {
		selectedClient,
		userWithClients,
		loadingClients,
		setSelectedClient,
	};

	return (
		<ClientAccessContext.Provider value={clientAccessContextValue}>
			<RequestInterceptor>{children}</RequestInterceptor>
		</ClientAccessContext.Provider>
	);
};

const useClientAccessContext = () => {
	const context = React.useContext(ClientAccessContext);
	if (context === undefined) {
		throw new Error(
			"useClientAccessContext must be used within a ClientContextProvider"
		);
	}
	return context;
};

export { ClientAccessProvider, useClientAccessContext };
